<template>
  <b-modal
    ref="configurationIncidentTypesModal"
    size="xxl"
    centered
    modal-class="configuration-modal-tabs configuration-incident-types-form"
    static
    hide-footer
    @hidden="hide"
  >
    <div slot="modal-title">
      {{ internalTitle }} <span style="text-transform: lowercase">{{ $tc('configuration.sections.incidentTypes', 1) }}</span>
    </div>
    <b-row class="px-0">
      <b-col class="px-2 pt-1 pb-2">
        <div>
          <b-tabs
            v-model="activeTabIndex"
            pills
            card
            vertical
            class="vt-tabs--pills"
          >
            <b-tab active>
              <template slot="title">
                <i
                  class="far fa-list-ul mr-2"
                  aria-hidden="true"
                />
                {{ $t('configuration.modal.basicData') }}
              </template>
              <b-row
                style=""
                class="scroll-area px-1 pt-1 pb-3"
              >
                <b-col class="mt-3">
                  <b-row>
                    <!--Incident Type translations-->
                    <b-col>
                      <!-- TODO: Este mensaje informativo solo aparecerá cuando se crea el tipo de incidencia -->
                      <div class="info-card d-flex align-items-center py-2 mb-3">
                        <div>
                          <i
                            class="fas fa-info-circle"
                            aria-hidden="true"
                          />
                        </div>
                        <div style="line-height: 14px">
                          <h3 class="label-dark">
                            {{ $t('configuration.modal.incidentTypeInformation') }}
                          </h3>
                        </div>
                      </div>
                      <div class="form-label">
                        <label>{{ $tc('configuration.sections.incidentTypes', 1) }}</label>
                      </div>
                      <configuration-name-translations :name-translations="internalIncidentType.nameTranslations" />
                    </b-col>
                    <!--Incident Type descriptions-->
                    <b-col
                      cols="12"
                      class="mt-3"
                    >
                      <div class="form-label">
                        <label>{{ $t('configuration.grid.description') }}</label>
                      </div>
                      <configuration-name-translations :name-translations="internalIncidentType.descriptionTranslations" />
                    </b-col>
                  </b-row>
                  <b-row class="mt-2">
                    <b-col
                      cols="12"
                      class="my-1 d-flex justify-content-end"
                    >
                      <div class="switch-btn mt-1 p-0 d-flex align-items-center">
                        <span class="mr-2">{{ internalIncidentType.active ? $tc('configuration.actions.active') : $tc('configuration.actions.inactive') }}</span>
                        <input
                          id="toggle"
                          v-model="internalIncidentType.active"
                          type="checkbox"
                          class="offscreen"
                          aria-hidden="true"
                        >
                        <label
                          for="toggle"
                          class="switch mb-0"
                        />
                      </div>
                    </b-col>
                  </b-row>
                </b-col>
              </b-row>
              <div class="footer-area component-bg d-flex justify-content-end px-3 py-2 align-items-center">
                <div>
                  <b-button
                    size="sm"
                    class="btn--primary"
                    :disabled="htmlDisabledIncidentTypeSaveButton"
                    @click="update"
                  >
                    <i
                      class="fal fa-check mr-2"
                      aria-hidden="true"
                    />
                    {{ internalTitle }}
                  </b-button>
                </div>
              </div>
            </b-tab>
            <b-tab :disabled="!internalIncidentType.id">
              <template slot="title">
                <i
                  class="far fa-map-marker-exclamation mr-2"
                  aria-hidden="true"
                />
                {{ $tc('configuration.sections.incidentOrigins', 2) }}
              </template>
              <b-row
                style="height: 100%"
                class="p-3"
              >
                <b-col cols="12">
                  <span class="title">{{ $tc('configuration.sections.incidentOrigins', 2) }}</span>
                </b-col>
                <b-col class="mt-2 tab-scroll-content without-footer association-table pl-0 pr-2 mx-2">
                  <div class="d-flex mb-1 header-row">
                    <div class="d-flex justify-content-between w-100">
                      <div class="fixed-column">
                        <vt-input
                          v-model="search.origins"
                          :name="$t('configuration.modal.filterIncidentsOrigins')"
                          class="form-control-sm"
                          :placeholder="$t('configuration.modal.filterIncidentsOrigins')"
                        />
                      </div>
                      <div
                        v-for="(coreEntity) in entitiesList"
                        :key="coreEntity.code"
                        class="check-column d-flex align-items-center"
                      >
                        <span class="pl-2">{{ coreEntity.translatedName }}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    v-for="(originRow) in filteredOriginRows"
                    :key="originRow.name"
                    class="d-flex"
                  >
                    <div class="d-flex justify-content-between bottom-line w-100">
                      <div class="fixed-column">
                        <span>{{ originRow.name }}</span>
                      </div>
                      <div
                        v-for="(originEntityRelation) in originRow.originEntityRelations"
                        :key="`${originRow.name}:${originEntityRelation.coreEntity.code}`"
                      >
                        <div class="check-column d-flex align-items-center">
                          <b-form-checkbox
                            v-model="originEntityRelation.assigned"
                            class="ml-2"
                            @change="incidentTypeOriginsChanged(originEntityRelation)"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </b-col>
              </b-row>
            </b-tab>
          </b-tabs>
        </div>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import ConfigurationNameTranslations from '@/modules/configurationModule/detail/ConfigurationNameTranslations'
import IncidentOriginsConfigurationServices from '@/api/resources/configuration/incidents/origins/IncidentOriginsConfigurationServices'
import IncidentCoreEntityService from '@/api/resources/configuration/incidents/IncidentCoreEntityService'
import IncidentTypesConfigurationOriginsRelationshipServices from '@/api/resources/configuration/incidents/types/IncidentTypesConfigurationOriginsRelationshipServices'
import languageService from '@/api/resources/common/languageService'
import { cloneDeep, isEmpty, uniqBy } from 'lodash'
import i18n from '@/lang/i18n'

const DATA_TAB = 0

export default {
    name: 'ConfigurationIncidentTypesModal',
    components: { ConfigurationNameTranslations },
    props: {
        actionTitle: String
    },
    data() {
        return {
            internalIncidentType: {},
            paginatedOrigins: [],
            originRows: [],
            activeTabIndex: DATA_TAB,
            entitiesList: [],
            search: {
                origins: '',
                entities: ''
            },
            internalTitle: ''
        }
    },
    computed: {
        htmlDisabledIncidentTypeSaveButton() {
            const defaultLanguage = this.internalIncidentType && this.internalIncidentType.nameTranslations && this.internalIncidentType.nameTranslations.find(name => name.language.defaultLanguage)
            const defaultLanguageDesc = this.internalIncidentType && this.internalIncidentType.descriptionTranslations && this.internalIncidentType.descriptionTranslations.find(name => name.language.defaultLanguage)
            return !(defaultLanguage?.value.trim().length) || !(defaultLanguageDesc?.value.trim().length)
        },
        filteredOriginRows() {
            return this.originRows.filter(origins => {
                return origins.name.toLowerCase().includes(this.search.origins.toLowerCase())
            })
        }
    },
    async created() {
        await this.loadCoreEntities()
    },
    methods: {
        async show(selectedOrigin) {
            this.activeTabIndex = 0
            this.internalTitle = this.actionTitle
            this.updateLoading(true)
            const [err, response] = await this.toCall(languageService.findAllActiveAndUiEnabled())
            this.updateLoading(false)
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
                return
            }
            const activeUILanguages = response
            if (selectedOrigin && !isEmpty(selectedOrigin)) {
                this.internalIncidentType = cloneDeep(selectedOrigin)
                await this.getIncidentTypeOriginRelations()
            } else {
                this.internalTitle = i18n.t('configuration.actions.add')
                this.clearData()
            }
            const defaultNameTranslations = activeUILanguages.map(lang => {
                return {
                    language: lang,
                    notes: '',
                    value: ''
                }
            })
            const nameTranslations = [].concat(this.internalIncidentType.nameTranslations, cloneDeep(defaultNameTranslations))
            const descriptionTranslations = [].concat(this.internalIncidentType.descriptionTranslations, cloneDeep(defaultNameTranslations))
            this.internalIncidentType.nameTranslations = uniqBy(nameTranslations, 'language.id')
            this.internalIncidentType.descriptionTranslations = uniqBy(descriptionTranslations, 'language.id')
            this.$refs.configurationIncidentTypesModal.show()
        },
        hide() {
            this.$refs.configurationIncidentTypesModal.hide()
        },
        clearData() {
            this.internalIncidentType = {
                active: true,
                id: '',
                name: '',
                nameTranslations: [],
                descriptionTranslations: []
            }
        },
        update() {
            if (this.internalIncidentType.id) {
                this.$emit('update', this.internalIncidentType)
            } else {
                this.$emit('create', this.internalIncidentType)
            }
        },
        async setNewIncidentType(newIncidentType) {
            this.internalIncidentType = {
                ...this.internalIncidentType,
                id: newIncidentType && newIncidentType.id
            }
            // Set activeTabIndex = 1 (Origins list)
            this.$nextTick(() => {
                this.activeTabIndex = 1
            })
            this.internalTitle = i18n.t('configuration.actions.update')
            await this.getIncidentTypeOriginRelations()
        },
        async getIncidentTypeOriginRelationsForCurrentIncidentType() {
            const response = await this.$_toCallUnique(IncidentTypesConfigurationOriginsRelationshipServices.getIncidentTypeOriginsRelationship(this.internalIncidentType.id))
            if (!response) {
                return []
            }
            return response
        },
        async filterOrigins() {
            // Return filtered list of Incident Origins
            const params = {}
            const [err, data] = await this.toCall(IncidentOriginsConfigurationServices.paginator({}, params))
            if (err) {
                this.showErrorModalGlobalFromErrorResponse(err)
            }
            this.paginatedOrigins = data && data.content
        },
        async loadCoreEntities() {
            this.entitiesList = await this.$_toCallUnique(IncidentCoreEntityService.getCoreEntities())
        },
        async incidentTypeOriginsChanged(incidentTypeOriginRelation) {
            if (incidentTypeOriginRelation.assigned) {
                const params = {
                    incidentOriginId: incidentTypeOriginRelation.incidentOriginId,
                    incidentTypeId: incidentTypeOriginRelation.incidentTypeId,
                    coreEntity: incidentTypeOriginRelation.coreEntity.code
                }
                const [err, response] = await this.toCall(IncidentTypesConfigurationOriginsRelationshipServices.createIncidentTypeOriginsRelationship(params))
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                } else {
                    this.showToastSuccessGlobal(this.$t('configuration.toastMessages.incidentTypeRelationshipOriginCreated'))
                    incidentTypeOriginRelation.id = response?.id
                }
            } else {
                const [err] = await this.toCall(IncidentTypesConfigurationOriginsRelationshipServices.deleteIncidentTypeOriginsRelationship(incidentTypeOriginRelation.id))
                if (err) {
                    this.showErrorModalGlobalFromErrorResponse(err)
                } else {
                    this.showToastSuccessGlobal(this.$t('configuration.toastMessages.incidentTypeRelationshipOriginDeleted'))
                }
            }
        },
        async getIncidentTypeOriginRelations() {
            await this.filterOrigins()
            let allIncidentTypeOriginRelationsOfCurrentIncidentType = await this.getIncidentTypeOriginRelationsForCurrentIncidentType()
            this.originRows = this.paginatedOrigins.map(origin => {
                const originEntityRelations = this.entitiesList.map(coreEntity => {
                    const foundRelation = allIncidentTypeOriginRelationsOfCurrentIncidentType.find(relation =>
                        relation.incidentType.id === this.internalIncidentType.id &&
                        relation.incidentOrigin.id === origin.id &&
                        relation.coreEntity.code === coreEntity.code
                    )
                    return {
                        id: foundRelation?.id,
                        incidentTypeId: this.internalIncidentType.id,
                        incidentOriginId: origin.id,
                        coreEntity: coreEntity,
                        assigned: !!foundRelation
                    }
                })
                return {
                    name: origin.name,
                    originEntityRelations
                }
            })
        }
    }
}
</script>

<style lang="scss">
    .configuration-incident-types-form .modal-body {
        border-radius: .3rem;
    }
    .configuration-incident-types-form .tab-content {
        height: calc(100vh - 330px);
        overflow-y: auto;
    }
    .configuration-incident-types-form .modal-content .card-header {
        width: 220px;
    }
    .configuration-incident-types-form .association-table .check-column {
        width: 80px;
    }
    .configuration-incident-types-form .info-card {
        border: 1px solid vt-color(grid-general-border);
        border-radius: 3px;
        border-left: 3px solid vt-color(primary);
        background: vt-color(primary, .9);
    }

    .configuration-incident-types-form .info-card i {
        color: vt-color(primary);
        font-size: font-size(very-big);
        margin: 0 10px;
    }

    .configuration-incident-types-form .info-card h3 {
        margin-bottom: 0;
        font-size: font-size(very-small);
        font-weight: bold;
    }

    .configuration-incident-types-form .info-card span {
        font-size: font-size(small);
    }

    .configuration-incident-types-form .translations-area {
        max-height: 100px;
        overflow-y: auto;
        padding-bottom: 3px;
    }
    @media only screen and (max-width: 1366px) and (min-width: 1360px) {
        .configuration-incident-types-form .tab-content {
            height: calc(100vh - 330px);
        }
    }
</style>
